import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../../../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../../../layout/pageLayout";
import { DesktopKpiStrip } from "../../../shared/DesktopKpiStrip";
import { usePermissions } from "../../../../hooks/usePermissions";
import {
  addCustomEstimateWorkType,
  addEstimateLocation,
  addEstimateWorkType,
  deleteEstimateLocation,
  deleteEstimateWorkType,
  formatLocationLabel,
  formatSurfaceTotals,
  loadAssemblyTemplatesForFeatureType,
  updateEstimateLocation,
  updateEstimateWorkTypeAssembly,
  updateEstimateWorkTypeName,
  workTypeLabel,
} from "../../../../helpers/estimates";
import { addEstimateWorkTypeFromTemplate } from "../../../../helpers/workTypeTemplates";
import { computeAreaBreakdown } from "../../../../helpers/estimateLinePricing";
import { presentedSubtotal } from "../../../../helpers/estimateTax";
import {
  computeEstimateTotals,
  formatCurrency,
} from "../../../../helpers/estimateTotals";
import { formatSurfaceType } from "../../../../helpers/measurementModel";
import {
  applyDrafts,
  findFeature,
  flattenFeatures,
  readEditorUrl,
  recallLastFeature,
  rememberLastFeature,
  syncEditorUrl,
  visibleLocations,
} from "./editorState";
import { JobTree } from "./JobTree";
import { SummaryPanel } from "./SummaryPanel";
import { MeasurementsTable } from "./MeasurementsTable";
import { MaterialsTable } from "./MaterialsTable";
import { LabourTable } from "./LabourTable";
import { DraftRegistryContext, createDraftRegistry } from "./draftRegistry";
import "../../../desktop/deskKit.css";
import "./desktopEstimateEditor.css";

function SaveIndicator({ state, unsaved }) {
  const text =
    state.phase === "saving" ? "Saving…"
    : state.phase === "error" ? `⚠ ${state.message || "Couldn't save"}`
    : unsaved ? "Not saved"
    : state.phase === "saved" ? "✓ All changes saved"
    : "Edits save automatically";
  const phase = state.phase === "saving" || state.phase === "error" ? state.phase : unsaved ? "dirty" : state.phase;
  return (
    <span className={`ed-save is-${phase}`} role="status" aria-live="polite" data-testid="save-indicator">
      {text}
    </span>
  );
}

/**
 * Desktop (≥1024px) single-screen estimate editor: job tree | feature grids | live summary.
 *
 * Everything here is composition: reads come from the already-loaded estimate
 * tree, writes use the existing helpers/estimates.js functions, and every
 * number comes from helpers/estimateTotals.js. Phones never mount this.
 */
export function DesktopEstimateEditor({
  estimateTree,
  materialsCatalog,
  featureTypes,
  workTypeTemplates,
  organizationId,
  clientName,
  estimateNumber,
  projectName,
  editable,
  onReload,
  onBack,
  onOpenPricing,
}) {
  const { actor } = usePermissions();
  const estimateId = estimateTree?.estimate_id;
  const locations = useMemo(() => visibleLocations(estimateTree), [estimateTree]);
  const flat = useMemo(() => flattenFeatures(estimateTree), [estimateTree]);

  /* ---------- selection (URL + remembered, no extra history entries) ---------- */
  const [selectedFeatureId, setSelectedFeatureId] = useState(() => {
    const fromUrl = readEditorUrl(window.location.search);
    const candidates = [
      fromUrl.estimateId === estimateId ? fromUrl.featureId : "",
      recallLastFeature(window, estimateId),
    ];
    const hit = candidates.find((id) => id && findFeature(estimateTree, id));
    return hit || flat[0]?.featureId || "";
  });

  const selected = selectedFeatureId ? findFeature(estimateTree, selectedFeatureId) : null;

  // If the selected feature disappears (deleted elsewhere) fall back to the first one.
  useEffect(() => {
    if (selectedFeatureId && !selected) setSelectedFeatureId(flat[0]?.featureId || "");
  }, [selectedFeatureId, selected, flat]);

  useEffect(() => {
    if (!estimateId) return;
    syncEditorUrl(window, { estimateId, featureId: selected ? selectedFeatureId : "" });
    if (selected) rememberLastFeature(window, estimateId, selectedFeatureId);
  }, [estimateId, selectedFeatureId, selected]);

  useEffect(
    () => () => syncEditorUrl(window, { estimateId: "", featureId: "" }),
    [],
  );

  /* ---------- save status + unsaved-draft mirror for the live summary ---------- */
  const [saveState, setSaveState] = useState({ phase: "idle", message: "" });
  const pending = useRef(0);
  const reportStatus = useCallback((phase, message = "") => {
    if (phase === "saving") {
      pending.current += 1;
      setSaveState({ phase: "saving", message: "" });
      return;
    }
    pending.current = Math.max(0, pending.current - 1);
    if (phase === "error") setSaveState({ phase: "error", message });
    else if (pending.current === 0) setSaveState({ phase: "saved", message: "" });
  }, []);

  const [drafts, setDrafts] = useState({ materials: {}, labour: {}, sections: {} });
  const [unsaved, setUnsaved] = useState(false);
  const setDraft = useCallback((kind, id, value) => {
    setDrafts((prev) => {
      const bucket = { ...prev[kind] };
      if (value) bucket[id] = value;
      else delete bucket[id];
      return { ...prev, [kind]: bucket };
    });
  }, []);

  /* ---------- totals: existing helpers only ---------- */
  const liveTree = useMemo(() => applyDrafts(estimateTree, drafts), [estimateTree, drafts]);
  const totals = useMemo(() => computeEstimateTotals(liveTree, materialsCatalog), [liveTree, materialsCatalog]);
  const estimateTotal = presentedSubtotal(liveTree, totals);

  const totalsByLocation = useMemo(() => {
    const map = {};
    totals.locations.forEach((loc) => { map[loc.locationId] = loc.locationTotal; });
    return map;
  }, [totals]);
  const featureTotals = useMemo(() => {
    const map = {};
    totals.locations.forEach((loc) => loc.workTypes.forEach((wt) => { map[wt.workTypeId] = wt; }));
    return map;
  }, [totals]);

  /* ---------- writes ---------- */
  const reload = useCallback(async () => {
    const result = await onReload?.();
    if (result && result.success === false) throw new Error(result.errorMessage || "Unable to refresh the estimate.");
  }, [onReload]);

  const mutate = useCallback(
    async (fn) => {
      reportStatus("saving");
      try {
        const out = await fn();
        await reload();
        reportStatus("saved");
        return out;
      } catch (err) {
        reportStatus("error", err?.message || "Couldn't save.");
        throw err;
      }
    },
    [reload, reportStatus],
  );

  /* ---------- pending drafts: flush before switching / leaving, warn on reload ---------- */
  const registry = useMemo(() => createDraftRegistry(), []);
  useEffect(() => registry.subscribe(() => setUnsaved(registry.hasUnsaved())), [registry]);
  const flushDrafts = useCallback(async () => {
    const ok = await registry.flushAll();
    if (ok) return true;
    // eslint-disable-next-line no-alert
    return window.confirm("Some lines are not saved yet (incomplete or failed). Leave them and continue?");
  }, [registry]);
  useEffect(() => {
    function onBeforeUnload(event) {
      if (!registry.hasUnsaved() && pending.current === 0) return undefined;
      registry.flushAll();
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [registry]);

  const select = useCallback(
    async (featureId) => {
      if (featureId === selectedFeatureId) return;
      if (registry.hasUnsaved() && !(await flushDrafts())) return;
      setSelectedFeatureId(featureId);
    },
    [flushDrafts, registry, selectedFeatureId],
  );
  const guarded = (fn) => async (...args) => {
    if (!registry.hasUnsaved() || (await flushDrafts())) fn?.(...args);
  };

  async function handleAddFeature(location, option) {
    const created = await mutate(() => {
      if (option.kind === "catalog") return addEstimateWorkType(actor, location.room_id, option.id);
      if (option.kind === "template") return addEstimateWorkTypeFromTemplate(location.room_id, option.id, organizationId);
      return addCustomEstimateWorkType(actor, location.room_id, option.name);
    });
    if (created?.estimate_feature_id) select(created.estimate_feature_id);
  }

  async function handleDeleteFeature(workType) {
    const id = workType.estimate_feature_id;
    const index = flat.findIndex((f) => f.featureId === id);
    await mutate(() => deleteEstimateWorkType(actor, id));
    if (id === selectedFeatureId) {
      const fallback = flat[index + 1] || flat[index - 1];
      setSelectedFeatureId(fallback?.featureId || "");
    }
  }

  async function handleDeleteLocation(location) {
    const ids = (location.workTypes || []).map((wt) => wt.estimate_feature_id);
    await mutate(() => deleteEstimateLocation(actor, location.room_id));
    if (ids.includes(selectedFeatureId)) setSelectedFeatureId("");
  }

  /* ---------- assembly picker (same helpers as the phone Feature screen) ---------- */
  const workType = selected?.workType || null;
  const liveWorkType = (selectedFeatureId ? findFeature(liveTree, selectedFeatureId)?.workType : null) || workType;
  const [assemblies, setAssemblies] = useState([]);
  const featureTypeId = workType?.feature_type_id || "";
  useEffect(() => {
    let cancelled = false;
    setAssemblies([]);
    if (!featureTypeId) return undefined;
    loadAssemblyTemplatesForFeatureType(featureTypeId)
      .then((rows) => { if (!cancelled) setAssemblies(rows || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [featureTypeId]);

  /* ---------- derived view data ---------- */
  const featureEntry = workType ? featureTotals[workType.estimate_feature_id] : null;
  const areaBreakdown = liveWorkType ? computeAreaBreakdown(liveWorkType) : [];
  const surfaceTotals = areaBreakdown.length
    ? Object.values(areaBreakdown.reduce((map, area) => {
      const key = area.section.surface_type;
      map[key] = map[key] || { surfaceType: key, label: formatSurfaceType(key), sqft: 0 };
      map[key].sqft += area.net;
      return map;
    }, {}))
    : (workType ? formatSurfaceTotals(workType.surface_totals) : []);
  const netSqft = areaBreakdown.length
    ? areaBreakdown.reduce((sum, area) => sum + area.net, 0)
    : (workType ? formatSurfaceTotals(workType.surface_totals).reduce((sum, row) => sum + row.sqft, 0) : 0);
  const position = flat.findIndex((f) => f.featureId === selectedFeatureId);
  const physicalLocationCount = (estimateTree?.locations || []).filter((l) => !l.is_default_scope).length;
  const featureCount = flat.length;

  const tableProps = workType
    ? { workType, liveWorkType, actor, editable, onChanged: reload, onStatus: reportStatus }
    : null;

  return (
    <CrewPilotModuleShell
      title={projectName || estimateTree?.title || "Estimate"}
      subtitle={[estimateNumber, clientName].filter(Boolean).join(" · ")}
      onBack={guarded(onBack)}
      desktopBackLabel="Estimate Summary"
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          <button type="button" className="dt-btn dt-btn--primary" onClick={guarded(onOpenPricing)}>
            Pricing &amp; Review
          </button>
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Estimate totals"
        className="est-d-kpi5"
        tiles={[
          { key: "mat", value: formatCurrency(totals.calculatedMaterialsTotal), label: "Materials", tone: "blue" },
          { key: "lab", value: formatCurrency(totals.calculatedLabourTotal), label: "Labour", tone: "muted" },
          { key: "total", value: formatCurrency(estimateTotal), label: "Estimate Total", tone: "gold" },
          { key: "locs", value: String(physicalLocationCount), label: physicalLocationCount === 1 ? "Location" : "Locations", tone: "green" },
          { key: "feats", value: String(featureCount), label: featureCount === 1 ? "Feature" : "Features", tone: "muted" },
        ]}
      />

      <DraftRegistryContext.Provider value={registry}>
      <div className="ed-grid" data-testid="desktop-estimate-editor">
        <JobTree
          locations={locations}
          featureTypes={featureTypes}
          templates={workTypeTemplates}
          selectedFeatureId={selectedFeatureId}
          totalsByLocation={totalsByLocation}
          totalsByFeature={Object.fromEntries(Object.entries(featureTotals).map(([id, wt]) => [id, wt.workTypeTotal]))}
          editable={editable}
          onSelectFeature={select}
          onAddLocation={(name) =>
            mutate(() => addEstimateLocation(actor, estimateId, name, (estimateTree.locations || []).length + 1))
          }
          onRenameLocation={(location, name) => mutate(() => updateEstimateLocation(actor, location.room_id, { name }))}
          onDeleteLocation={handleDeleteLocation}
          onAddFeature={handleAddFeature}
          onRenameFeature={(wt, name) => mutate(() => updateEstimateWorkTypeName(actor, wt.estimate_feature_id, name))}
          onDeleteFeature={handleDeleteFeature}
        />

        <main className="ed-main" aria-label="Selected feature">
          {!workType ? (
            <div className="ed-empty">
              <h2>{featureCount === 0 ? "Add your first feature" : "Select a feature"}</h2>
              <p>
                {featureCount === 0
                  ? "Add a location, then a feature under it, to start entering measurements, materials and labour."
                  : "Pick a feature in the job tree to edit it here."}
              </p>
            </div>
          ) : (
            <>
              <header className="ed-feature-head">
                <div className="ed-feature-head__title">
                  <p className="ed-crumb">{formatLocationLabel(selected.location)}</p>
                  <h2>{workTypeLabel(workType)}</h2>
                </div>
                <dl className="ed-feature-head__totals">
                  <div><dt>Materials</dt><dd data-testid="feature-materials">{formatCurrency(featureEntry?.materialsTotal)}</dd></div>
                  <div><dt>Labour</dt><dd data-testid="feature-labour">{formatCurrency(featureEntry?.labourTotal)}</dd></div>
                  <div className="is-emph"><dt>Total</dt><dd data-testid="feature-total">{formatCurrency(featureEntry?.workTypeTotal)}</dd></div>
                </dl>
                <div className="ed-feature-head__tools">
                  <SaveIndicator state={saveState} unsaved={unsaved} />
                  <div className="ed-pager" role="group" aria-label="Move between features">
                    <button type="button" className="dt-btn" disabled={position <= 0}
                      onClick={() => select(flat[position - 1].featureId)}>← Previous</button>
                    <span className="ed-pager__pos">{position + 1} / {featureCount}</span>
                    <button type="button" className="dt-btn" disabled={position < 0 || position >= featureCount - 1}
                      onClick={() => select(flat[position + 1].featureId)}>Next →</button>
                  </div>
                </div>
              </header>

              {assemblies.length > 0 ? (
                <div className="ed-assembly">
                  <label htmlFor="ed-assembly-select">Assembly</label>
                  <select
                    id="ed-assembly-select"
                    className="eg-input"
                    value={workType.assembly_template_id || ""}
                    disabled={!editable}
                    onChange={(event) =>
                      mutate(() => updateEstimateWorkTypeAssembly(actor, workType.estimate_feature_id, event.target.value || null)).catch(() => {})
                    }
                  >
                    <option value="">No assembly</option>
                    {assemblies.map((a) => (
                      <option key={a.assembly_template_id} value={a.assembly_template_id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* key=feature so row drafts never leak between features */}
              <div key={workType.estimate_feature_id} className="ed-sections">
                <MeasurementsTable {...tableProps} onDraft={(id, value) => setDraft("sections", id, value)} />
                <MaterialsTable {...tableProps} materialsCatalog={materialsCatalog}
                  onDraft={(id, value) => setDraft("materials", id, value)} />
                <LabourTable {...tableProps} materialsCatalog={materialsCatalog}
                  onDraft={(id, value) => setDraft("labour", id, value)} />
              </div>
            </>
          )}
        </main>

        <SummaryPanel
          featureName={workType ? workTypeLabel(workType) : ""}
          featureTotal={featureEntry?.workTypeTotal || 0}
          locationName={selected ? formatLocationLabel(selected.location) : ""}
          locationTotal={selected ? totalsByLocation[selected.location.room_id] || 0 : 0}
          estimateTotal={estimateTotal}
          surfaceTotals={surfaceTotals}
          netSqft={netSqft}
        />
      </div>
      </DraftRegistryContext.Provider>
    </CrewPilotModuleShell>
  );
}
